// packages/web/src/components/admin/approver-picker.tsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface ApproverCandidate {
  id: string;
  name: string;
  email: string;
}

export interface ApproverPickerProps {
  value: string | null;
  onChange: (id: string | null) => void;
  candidates: ApproverCandidate[];
  exclude?: string[];
  placeholder?: string;
  disabled?: boolean;
}

export function ApproverPicker({
  value,
  onChange,
  candidates,
  exclude = [],
  placeholder,
  disabled,
}: ApproverPickerProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("admin.replacements.approver-picker.placeholder");
  const [open, setOpen] = useState(false);
  const excludeSet = new Set(exclude);
  const visible = candidates.filter((c) => !excludeSet.has(c.id));
  const selected = candidates.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className="w-full justify-between"
        >
          {selected ? selected.name : resolvedPlaceholder}
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("admin.replacements.approver-picker.search-placeholder")} />
          <CommandList>
            <CommandEmpty>{t("admin.replacements.approver-picker.empty")}</CommandEmpty>
            <CommandGroup>
              {visible.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  onSelect={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 size-4", value === c.id ? "opacity-100" : "opacity-0")}
                  />
                  <span className="flex flex-col">
                    <span>{c.name}</span>
                    <span className="text-[12px] text-ink-500">{c.email}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
